import type { ReactNode } from 'react'
import { Suspense } from 'react'
import { NavLink, Outlet } from 'react-router'

import { EsqueletoDeTela } from '@/components/esqueleto'
import type { Aba } from '@/app/navigation'
import { ClienteSelector } from '@/app/layout/ClienteSelector'
import { useAssistenteVisivel } from '@/assistente/visibilidade'

interface ModuleFrameProps {
  titulo: string
  abas: Aba[]
  /** Base path the tab segments hang off, e.g. `/cliente/abc/finops`. */
  base: string
  /** Absent on `/admin/*` and on `/compliance/perguntas` — both global (BR-23). */
  mostrarSeletorCliente: boolean
  /** Right-hand slot: `exportar pdf` on `2c`, `novo cliente` on `2n`. */
  acoes?: ReactNode
  /** Left-hand slot after the title, where a module has no tabs — the cycle selector on `2l`. */
  extrasEsquerda?: ReactNode
  /**
   * Where a tab links to. Defaults to `${base}/${segmento}`; Compliance overrides it
   * because one of its tabs is global (`/compliance/perguntas`) and the others must
   * carry the `?padrao=` selection across the switch.
   */
  destinoDaAba?: (aba: Aba) => string
}

/**
 * The module frame — title, tab bar, client selector and actions, per shot `2c`.
 *
 * It renders ABOVE the `<Outlet/>` as a layout route, which is what makes it persist
 * across tabs: switching tab swaps only the outlet, and the frame is never remounted.
 * Rendering it inside each screen would look identical and reflow on every click.
 *
 * The right-hand slot is deliberately a slot rather than a fixed `exportar pdf`:
 * shot `2c` puts the export there, shot `2n` puts `novo cliente` there. Same bar,
 * different content per module.
 */
export function ModuleFrame({
  titulo,
  abas,
  base,
  mostrarSeletorCliente,
  acoes,
  extrasEsquerda,
  destinoDaAba,
}: ModuleFrameProps) {
  const assistenteVisivel = useAssistenteVisivel()

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <header className="border-border-subtle bg-surface-card flex items-center gap-6 border-b px-8 py-4">
        <h1 className="type-h3 text-text-primary shrink-0">{titulo}</h1>

        {/* ⚠️ `overflow-x-auto` junto com `min-w-0`: sem ele os links têm
            `whitespace-nowrap` e VAZAM para fora da nav encolhida, passando por
            baixo do seletor de cliente. Aparece a partir de ~1040px com as
            cinco abas do FinOps e um nome de cliente longo — que é exatamente o
            caso real do "Tribunal de Justiça (exemplo)". */}
        {abas.length > 0 && (
          <nav
            aria-label={`Abas de ${titulo}`}
            className="flex min-w-0 items-center gap-6 overflow-x-auto"
          >
            {abas.map((aba) => (
              <NavLink
                key={aba.segmento}
                to={destinoDaAba ? destinoDaAba(aba) : `${base}/${aba.segmento}`}
                className={({ isActive }) =>
                  [
                    'border-b-2 pb-1 whitespace-nowrap type-body transition-colors duration-base ease-out',
                    isActive
                      ? 'border-iox-navy text-iox-navy font-semibold'
                      : 'text-text-muted hover:text-text-body border-transparent',
                  ].join(' ')
                }
              >
                {aba.icone && <i className={`ph ph-${aba.icone} mr-1.5`} aria-hidden="true" />}
                {aba.rotulo}
              </NavLink>
            ))}
          </nav>
        )}

        {extrasEsquerda}

        <div className="ml-auto flex shrink-0 items-center gap-4">
          {mostrarSeletorCliente && <ClienteSelector />}
          {acoes}
        </div>
      </header>

      {/* `pb-24` quando o botão do assistente está na tela: ele flutua sobre o canto
          inferior direito (24px de recuo + 56px de botão), e sem este respiro a
          última linha de uma tabela fica embaixo dele, sem como rolar para fora. */}
      <div className={`min-w-0 flex-1 overflow-auto p-8 ${assistenteVisivel ? 'pb-24' : ''}`}>
        {/* As telas com gráfico entram por `lazy` (ver modules/finops/pages).
            O Suspense fica AQUI, no frame, e não em cada tela: assim a tab bar e
            o seletor de cliente continuam desenhados enquanto o chunk carrega —
            em vez de a página inteira piscar. */}
        <Suspense fallback={<EsqueletoDeTela />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  )
}
